import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText, Receipt, CalendarClock, Target, ListChecks, ExternalLink,
  CreditCard, Clock, CheckCircle2, Loader2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const map = {
    pending_review: { label: "Under review", cls: "bg-amber-100 text-amber-800 border-amber-200" },
    approved: { label: "Approved", cls: "bg-green-100 text-green-800 border-green-200" },
    in_progress: { label: "In progress", cls: "bg-blue-100 text-blue-800 border-blue-200" },
    completed: { label: "Completed", cls: "bg-stone-200 text-stone-700 border-stone-300" },
    declined: { label: "Declined", cls: "bg-red-100 text-red-800 border-red-200" },
  };
  const m = map[status] || { label: status, cls: "bg-stone-100 text-stone-700" };
  return <Badge className={`rounded-full border ${m.cls}`} data-testid="application-status-badge">{m.label}</Badge>;
}

export default function ClientDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard/client");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const payInvoice = async (paymentId) => {
    setPayingId(paymentId);
    try {
      const { data } = await api.post(`/payments/${paymentId}/checkout`, {}, { params: { origin_url: window.location.origin } });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPayingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="client-dashboard-loading">
        <Loader2 className="animate-spin text-amber-700" size={28} strokeWidth={1.5} />
      </div>
    );
  }

  const { stats, sessions, goals, tasks, application, contract, payments, clickup_url } = data;

  return (
    <div data-testid="client-dashboard" className="space-y-10">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-2">Client Portal</div>
        <h1 className="font-serif text-4xl text-stone-900 tracking-tight">Your journey</h1>
      </div>

      {application && !["approved", "in_progress", "completed"].includes(application.status) && (
        <div data-testid="application-status-card" className="rounded-3xl border border-amber-200 bg-amber-50 p-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-sm text-stone-700">Your application status</div>
            <div className="mt-2"><StatusBadge status={application.status} /></div>
            {application.status === "pending_review" && (
              <p className="text-xs text-stone-500 mt-2">Hufrey typically reviews new applications within 1-2 business days.</p>
            )}
            {application.status === "declined" && application.admin_notes && (
              <p className="text-xs text-stone-500 mt-2">Note: {application.admin_notes}</p>
            )}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: CalendarClock, label: "Upcoming sessions", value: stats.active_bookings },
          { icon: CheckCircle2, label: "Completed sessions", value: stats.completed_sessions },
          { icon: Target, label: "Active goals", value: stats.active_goals },
          { icon: ListChecks, label: "Open tasks", value: stats.open_tasks },
        ].map((s) => (
          <div key={s.label} data-testid={`client-stat-${s.label.replace(/\s/g, "-").toLowerCase()}`} className="rounded-2xl border border-stone-200 bg-white p-5">
            <s.icon size={18} strokeWidth={1.5} className="text-amber-700" />
            <div className="font-serif text-2xl text-stone-900 mt-3">{s.value}</div>
            <div className="text-xs text-stone-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Contract */}
        <div data-testid="contract-card" className="rounded-3xl border border-stone-200 bg-white p-6">
          <div className="flex items-center gap-2 text-stone-900">
            <FileText size={17} strokeWidth={1.5} className="text-amber-700" />
            <h2 className="font-serif text-xl">Your contract</h2>
          </div>
          {contract ? (
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-stone-500">Package</span><span className="text-stone-900">{contract.package_name}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Monthly fee</span><span className="text-stone-900">${contract.monthly_fee.toLocaleString()} CAD</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Term</span><span className="text-stone-900">{contract.term_months} months</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Start</span><span className="text-stone-900">{contract.start_date?.slice(0, 10)}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">End</span><span className="text-stone-900">{contract.end_date?.slice(0, 10)}</span></div>
              <div className="flex justify-between items-center"><span className="text-stone-500">Status</span><Badge className="rounded-full bg-green-100 text-green-800 border border-green-200">{contract.status}</Badge></div>
              <Link to="/legal/terms" target="_blank" className="inline-flex items-center gap-1 text-xs text-amber-700 hover:underline mt-2">
                Full Terms & Client Agreement <ExternalLink size={11} strokeWidth={1.5} />
              </Link>
            </div>
          ) : (
            <p className="text-sm text-stone-500 mt-4">
              Your coaching agreement will appear here once your application is approved and your package is confirmed.
            </p>
          )}
        </div>

        {/* ClickUp */}
        <div data-testid="clickup-card" className="rounded-3xl border border-stone-200 bg-white p-6">
          <div className="flex items-center gap-2 text-stone-900">
            <ListChecks size={17} strokeWidth={1.5} className="text-amber-700" />
            <h2 className="font-serif text-xl">Tasks & follow-up</h2>
          </div>
          {clickup_url ? (
            <>
              <p className="text-sm text-stone-500 mt-3">Your personal task board is managed in ClickUp.</p>
              <a href={clickup_url} target="_blank" rel="noopener noreferrer" data-testid="clickup-open-link">
                <Button className="mt-4 rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50">
                  Open ClickUp board <ExternalLink size={14} strokeWidth={1.5} className="ml-2" />
                </Button>
              </a>
            </>
          ) : (
            <p className="text-sm text-stone-500 mt-3">
              Once your coaching starts, Hufrey will link your ClickUp board here for tasks and follow-up.
            </p>
          )}

          {tasks.length > 0 && (
            <div className="mt-5 space-y-2 border-t border-stone-100 pt-4">
              {tasks.slice(0, 5).map((t) => (
                <div key={t.id} data-testid={`task-item-${t.id}`} className="flex items-center gap-2 text-sm">
                  <span className={`h-1.5 w-1.5 rounded-full ${t.completed ? "bg-green-700" : "bg-amber-500"}`} />
                  <span className={t.completed ? "line-through text-stone-400" : "text-stone-700"}>{t.title}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Goals */}
      {goals.length > 0 && (
        <div data-testid="goals-card" className="rounded-3xl border border-stone-200 bg-white p-6">
          <div className="flex items-center gap-2 text-stone-900 mb-4">
            <Target size={17} strokeWidth={1.5} className="text-amber-700" />
            <h2 className="font-serif text-xl">Your goals</h2>
          </div>
          <div className="space-y-4">
            {goals.map((g) => (
              <div key={g.id} data-testid={`goal-item-${g.id}`}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-stone-900">{g.title}</span>
                  <span className="text-stone-500">{g.progress}%</span>
                </div>
                <Progress value={g.progress} className="h-1.5" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Receipts */}
      <div data-testid="payments-card" className="rounded-3xl border border-stone-200 bg-white p-6">
        <div className="flex items-center gap-2 text-stone-900 mb-4">
          <Receipt size={17} strokeWidth={1.5} className="text-amber-700" />
          <h2 className="font-serif text-xl">Receipts & payments</h2>
        </div>
        {payments.length === 0 ? (
          <p className="text-sm text-stone-500">No invoices yet.</p>
        ) : (
          <div className="divide-y divide-stone-100">
            {payments.map((p) => (
              <div key={p.id} data-testid={`payment-item-${p.id}`} className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm text-stone-900">{p.description}</div>
                  <div className="text-xs text-stone-500 mt-0.5">{p.period_label || p.created_at.slice(0, 10)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-serif text-stone-900">${p.amount.toLocaleString()} CAD</span>
                  {p.status === "paid" ? (
                    <Badge className="rounded-full bg-green-100 text-green-800 border border-green-200">Paid</Badge>
                  ) : (
                    <Button
                      size="sm"
                      data-testid={`pay-invoice-${p.id}`}
                      disabled={payingId === p.id}
                      onClick={() => payInvoice(p.id)}
                      className="rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50 h-8"
                    >
                      <CreditCard size={13} strokeWidth={1.5} className="mr-1.5" />
                      {payingId === p.id ? "Redirecting..." : "Pay now"}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sessions */}
      <div data-testid="sessions-card" className="rounded-3xl border border-stone-200 bg-white p-6">
        <div className="flex items-center gap-2 text-stone-900 mb-4">
          <Clock size={17} strokeWidth={1.5} className="text-amber-700" />
          <h2 className="font-serif text-xl">Session records</h2>
        </div>
        {sessions.length === 0 ? (
          <p className="text-sm text-stone-500">No sessions yet.</p>
        ) : (
          <div className="divide-y divide-stone-100">
            {sessions.map((s) => (
              <div key={s.id} data-testid={`session-item-${s.id}`} className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm text-stone-900">{s.service_title}</div>
                  <div className="text-xs text-stone-500 mt-0.5">{s.scheduled_at ? new Date(s.scheduled_at).toLocaleString() : "Not yet scheduled"}</div>
                </div>
                <Badge className="rounded-full bg-stone-100 text-stone-700 border border-stone-200 capitalize">{s.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
